/* eslint-disable complexity */
import { memo, useCallback, useMemo } from "react";
import { type SrpProductData } from "../../../../../utils/models";
import { Card } from "../../../../../Configurators/Linear/components/Card";
import {
  AliasesParameter,
  GPSDParameter,
  OldDesignationParameter,
  SchaefflerIdParameter,
  SearchPageParamContainer,
} from "../../../../../common/components/Product/ProductParams";
import { PriceReferenceParam } from "~/feature/cart/cart/components/LineItemEditableParams";
import { QuantityInput } from "../../../../../common/components/QuantityInput/QuantityInput";
import { MAX_QUANTITY } from "../../../../../utils/constants";
import CircleSpinner from "../../../../../common/components/CircleSpinner/CircleSpinner";
import {
  getSurcharge,
  isProductPMA,
  type SelectNetPriceProps,
} from "../../../../../feature/ProductDescriptor/v1/productDescriptorUtils";
import { PackagingSelection } from "../../../../../common/components/PackagingSelection/PackagingSelection";

import { addToCartApi } from "../../../../../AddToCart/redux/addToCart/actions";
import { getCanUserAddToCart } from "../../../../../ProductDetails/components/ProductSummary/ExtendedPdpSummary";
import { canSeePriceRefColumn } from "../../../../../QuickEntryPopup/helpers";
import { usePackageRestrictionContext } from "../../../../../common/components/PackageRestrictionContext/PackageRestrictionContext";
import {
  isProductComparisonCheckboxVisible,
  ProductComparisonCheckbox,
} from "../../../../../ProductComparison/ProductComparisonCheckbox";
import { NewVersionMessage } from "../../../../../common/components/Messages/NewVersionMessage";
import { useAddToCartForm } from "../../../../../AddToCart/hooks/useAddToCartNew";
import { canSeeAddToCartButton, canSeeListPrice, canSeeWishlistButton } from "../../../../../utils/features";
import { trackUserClickRecommendedProductSectionDetails } from "../../../../../ProductDetails/RecommendedProducts/recommendedProductAnalytics";

import classes from "./SearchResultProductCustomer.module.less";
import classNames from "classnames";
import CenterContents from "../../../../../common/components/CenterContents";
import { isSpecialPrice } from "~/ProductDetails/redux/productDetailsSelectors";
import { useTranslation } from "react-i18next";
import { SRPPackageRestrictionContext } from "~/common/components/PackageRestrictionContext/SRPPackageRestrictionContext";
import { getGALocationForSearchItem, SearchPageType, useSearchPageType } from "~/Search/useSearchPageType";
import {
  CardContent,
  ListPrice,
  ProductDetailsLayout,
} from "~/Search/components/ProductResults/views/ListView/components/SearchResultItemCommonElements";
import { SaleTag } from "~/SalesCampaign/components/SaleTag/SaleTag";
import { SalesDataProvider } from "~/SalesCampaign/useSalesData";
import { calculateSalesCampaign4PDP, type PdpSaleData, processPricingDetails } from "~/SalesCampaign/utils";
import { Price as PriceV3 } from "~/common/components/Price/v3/Price";
import { SaleDiscountIndicator } from "~/SalesCampaign/components/SaleDiscountIndicator/SaleDiscountIndicator";
import { SaleQuantityIndicator } from "~/SalesCampaign/components/SaleQuantityIndicator/SaleQuantityIndicator";
import { Controller } from "react-hook-form";
import { CheckboxWithLabel } from "~/common/components/CheckboxWithLabel";
import { makeSalesAwarePackCodeProvider } from "~/SalesCampaign/salesAwarePackCodeProvider";
import { ProductDescriptorContext } from "~/feature/Product/ProductContext";
import { PackagingSelectionVariant } from "~/common/components/PackagingSelection/PackagingSelectionVariant";
import { zero } from "~/utils/PriceUtils";
import { selectSalesPrice } from "~/SalesCampaign/selectors";
import { useSelector } from "react-redux";
import { Product } from "~/feature/Product";
import { SearchTechValueTable } from "~/Search/components/ProductResults/views/ListView/components/SearchTechValueTable";
import { Button, Stack, styled, Typography } from "@mui/material";
import { Alert } from "~/common/m3/Alert/Alert";
import { useResultTileZoovuParams } from "~/Search/utils/searchUtils";
import { usePurchasePlace, usePurchaseType } from "~/feature/Analytics/ga/recommendations/RecommendationContexts";
import { getPurchaseDataFromUrl } from "~/ProductDetails/components/ProductSummary/PdpSummaryHelper";
import { isTariffSurchargeEnabled } from "~/feature/cart/cart/utils/cartUtils";
import RichTooltip from "~/common/components/RichTooltip/RichTooltip";
import Cycle from "~/common/icons/Cycle";
import parse from "html-react-parser";

import MMDAssistant from "~/ProductDetails/components/MMDAssistant/MMDAssistant";
import { useSalesHelper } from "~/SalesCampaign/salesHelper";
import { PriceElements, type PriceProps } from "~/common/components/Price/PriceV4";
import { FontSize, getFontStyle } from "~/common/components/materialInput/Fonts";
import { SaleEndIndicator } from "~/SalesCampaign/components/SaleEndIndicator/SaleEndIndicator";
import { URL_RECONDITIONABLE } from "~/utils/url/staticUrls";
import Tag from "~/common/components/Tag/Tag";
import { useSrpCmnLogic } from "~/ProductDetails/components/MMDAssistant/logic/useSrpCmnLogic";
import { useAddProductToWishlistFlow } from "~/common/hooks/useAddProductToWishlistFlow";
import { renderButtonsNew } from "./components/useSrpButtons";

interface Props {
  isPdp?: boolean;
  productCode: string;
  index: number;
  onProductClick: (area: string, product: SrpProductData) => void;
  searchQuery: string;
}

export const SearchResultProductCustomer = memo((props: Props) => {
  return (
    <SRPPackageRestrictionContext productCode={props.productCode}>
      <SingleResultRow {...props} />
    </SRPPackageRestrictionContext>
  );
});

const SingleResultRow = ({ productCode, onProductClick, isPdp }: Props) => {
  const { t } = useTranslation();

  const reco_place = usePurchasePlace();
  const reco_type = usePurchaseType();
  const pageType = useSearchPageType();
  const gaLocation = getGALocationForSearchItem(isPdp, pageType);

  const restrictedPackage = usePackageRestrictionContext();
  const [formProps, productDescriptor] = useAddToCartForm({
    productCode,
    initialPackCodeProvider: makeSalesAwarePackCodeProvider(restrictedPackage),
  });

  const quantity = formProps.watch("quantity");

  const selectedPack = formProps.watch("selectedPack");
  const packCode = formProps.watch("selectedPackCode");
  const packSize = selectedPack?.packSize ?? 1;

  const salesHelper = useSalesHelper(productDescriptor);
  const isProductOnSale = salesHelper.isProductOnSale();
  const isPackageOnSale = salesHelper.isPackageOnSale(packCode);

  const salesStock = salesHelper.forPackage(packCode)?.stock ?? 0;
  const exceedsSalesStock = salesStock > 0 && quantity > salesStock;

  const salesData = useMemo(
    () => calculateSalesCampaign4PDP({ productDescriptor, includeSoldOut: true }),
    [productDescriptor],
  );

  const product = productDescriptor.base;

  const tariffSurcharge = isTariffSurchargeEnabled() && formProps.watch("tariffSurcharge");

  const addToWishlistAvailable = canSeeWishlistButton();
  const addToCartAvailable = canSeeAddToCartButton();

  const saleForPack: PdpSaleData | undefined = salesData.data?.salePerPack[packCode];
  const saleActive = isPackageOnSale && formProps.watch("salePriceEnabled");

  const { recommendationPlace, recommendationType } = getPurchaseDataFromUrl();

  const doAddToCart = () => {
    const formValues = formProps.getValues();
    if (isPdp) {
      trackUserClickRecommendedProductSectionDetails(product.designation);
    }
    const context = {
      page: reco_place,
      recommendationPlace,
      recommendationType,
    };
    addToCartApi({
      restrictionQuery: restrictedPackage ?? "",
      productCode,
      ...formValues,
      addToCartSource: pageType === SearchPageType.SEARCH ? "srp" : "unknown",
      context,
    });
  };

  const productInformationClick = useCallback(
    () => onProductClick("Product information", product),
    [onProductClick, product],
  );

  let isPackageSizeInvalid = false;
  if (selectedPack) {
    isPackageSizeInvalid = quantity === 0 || quantity % selectedPack.packSize > 0;
  }

  const userCanAddToCart = getCanUserAddToCart(productDescriptor, selectedPack);

  const selectedPackageCode = formProps.watch("selectedPackCode");
  const onSelectPackage = (packCode: string) => {
    formProps.setValue("selectedPackCode", packCode);
  };

  const hasNotifications = isPackageSizeInvalid || exceedsSalesStock;

  const isSpecial = isSpecialPrice(productDescriptor, packCode);
  const hasSurcharge = getSurcharge({ product: productDescriptor, packCode, totalQuantity: packSize }) > 0;

  const webservicesDisabled = window.SCHA.config.disableAllWS;
  const addToCartDisabled =
    webservicesDisabled ||
    !userCanAddToCart ||
    isPackageSizeInvalid ||
    productDescriptor.isLoading ||
    productDescriptor.isError;

  const priceReference = formProps.watch("priceReference");
  const priceParams: SelectNetPriceProps = {
    packCode,
    extraQuantity: quantity,
    priceReference,
    productCode,
  };

  const salesPrice = useSelector(selectSalesPrice({ ...priceParams, salesData: saleForPack })) ?? zero();

  const { price, oldPrice, netPrice, discountFormatted } = processPricingDetails({
    netPrice: formProps.watch("netPrice"),
    salesPrice,
    saleActivated: saleActive,
  });

  const showListPrice =
    canSeeListPrice({
      product: productDescriptor,
      hasSurcharge: hasSurcharge,
      isSpecialPrice: isSpecial,
    }) && !saleActive;

  const pdpLinkProps = {
    onClick: productInformationClick,
    reco_type: recommendationType,
    reco_place: recommendationPlace,
  };

  const mmdCmnData = useSrpCmnLogic(productDescriptor.base);

  const [openAddToWishlist, addToWishlistModal] = useAddProductToWishlistFlow({
    productInput: product,
    initialQuantity: formProps.watch("quantity"),
    initialPriceReference: formProps.watch("priceReference"),
  });

  const buttons = renderButtonsNew(productDescriptor, {
    atc: {
      onClick: doAddToCart,
      disabled: addToCartDisabled,
    },
    wishlist: {
      onClick: () => openAddToWishlist(),
    },
  }).buttons;

  const additionalPdpUrlParams = mmdCmnData.customerMaterialNumbers
    ? { cmn: mmdCmnData.customerMaterialNumbers[0] }
    : ({} as Record<string, string>);

  return (
    <>
      {addToWishlistModal}
      <SalesDataProvider value={salesData.data}>
        <ProductDescriptorContext value={productDescriptor}>
          <Card
            className={classNames(classes.root, "jsSearchResultProduct")}
            data-test-id={`search-result-item`}
            {...useResultTileZoovuParams(product)}
          >
            {isProductOnSale && <SaleTag className={classes.salesTag} size={32} />}
            <CardContent>
              <ProductDetailsLayout
                imageSlot={
                  <Product.PdpLink {...pdpLinkProps} additionalParams={additionalPdpUrlParams}>
                    <Product.PrimaryImageWithLogos className={classes.image} clickable={false} />
                  </Product.PdpLink>
                }
                productDataSlot={
                  <Stack gap={2}>
                    <Stack gap={1}>
                      <Product.PdpLink {...pdpLinkProps} additionalParams={additionalPdpUrlParams}>
                        <Product.FullDesignation clickable={false} className={classes.designation} />
                      </Product.PdpLink>

                      <Product.MMDChip />

                      <Product.PdpLink {...pdpLinkProps} additionalParams={additionalPdpUrlParams}>
                        <Product.ShortDescription clickable={false} />
                      </Product.PdpLink>

                      {product.reconditionable && (
                        <RichTooltip
                          content={
                            <Typography>
                              {parse(t("cmsnavigationnode.schaefflerContentCatalog.reconditionableInfo"))}
                            </Typography>
                          }
                          actions={[
                            <Button href={URL_RECONDITIONABLE} variant="text">
                              {t("cmsnavigationnode.schaefflerContentCatalog.moreInfo")}
                            </Button>,
                          ]}
                        >
                          <Tag icon={<Cycle fontSize="small" />} iconPosition="start" variant="success">
                            {t("cmsnavigationnode.schaefflerContentCatalog.reconditionable")}
                          </Tag>
                        </RichTooltip>
                      )}

                      {saleForPack && <StyledSaleEndIndicator endDate={saleForPack?.endDate} />}
                    </Stack>

                    <NewVersionMessage className={classes.newVersionMessage} baseProduct={product} />

                    <SearchPageParamContainer>
                      <OldDesignationParameter variant={"search-page"} label={t("product.oldDesignations")} />
                      <SchaefflerIdParameter variant={"search-page"} />
                      <AliasesParameter variant={"search-page"} mode={"13-digits"} />
                    </SearchPageParamContainer>
                  </Stack>
                }
                priceDataSlot={
                  <div className={classes.priceCol}>
                    {productDescriptor.isLoading && (
                      <div className={"mb-30"}>
                        <CircleSpinner />
                      </div>
                    )}
                    {productDescriptor.isLoaded && (
                      <PriceElements.Stack gap={0}>
                        <Stack direction="row" gap={1} alignSelf={"flex-end"}>
                          <SaleDiscountIndicator.V2 discountFormatted={discountFormatted} />
                          <CurrentPrice
                            price={price}
                            zeroText={t("product.priceOnRequest")}
                            tariffSurcharge={tariffSurcharge}
                          />
                        </Stack>
                        {oldPrice && (
                          <OldPrice price={oldPrice} variant="bodySmall" tariffSurcharge={tariffSurcharge} />
                        )}
                        <PriceElements.NetPriceLabel price={netPrice} />
                        {tariffSurcharge && <p className="label-small">{t("price.tariff.surcharge.applicable")}</p>}
                        {!saleActive && (
                          <PriceV3.ExtraLabels
                            className={classes.extraPriceLabels}
                            price={netPrice}
                            surchargeEnabled={false}
                          />
                        )}
                      </PriceElements.Stack>
                    )}
                    {saleActive && <SaleQuantityIndicator.V2 quantity={salesStock} />}

                    {showListPrice && <ListPrice product={productDescriptor} packCode={packCode} />}
                    <GPSDParameter product={productDescriptor.base} />
                  </div>
                }
                actionSlot={
                  <Stack gap={1}>
                    <Stack gap={2}>
                      <QuantityInput
                        min={packSize}
                        max={MAX_QUANTITY}
                        value={quantity}
                        disabled={productDescriptor.isLoading || !product.quantityVisible}
                        step={packSize}
                        data-test-id={"plp-srp-list-item-input-quantity"}
                        onValueChange={(value) =>
                          formProps.setValue("quantity", value, { shouldTouch: true, shouldDirty: true })
                        }
                        error={isPackageSizeInvalid}
                        variant="compact"
                      />

                      <PriceReferenceParam
                        product={product}
                        priceReference={priceReference}
                        data-test-id={"plp-srp-list-item-price-reference"}
                        onChange={(value) => formProps.setValue("priceReference", value ?? "")}
                        editable={canSeePriceRefColumn()}
                        disabled={saleActive}
                      />
                    </Stack>
                    <Stack>
                      {isPackageOnSale && (
                        <Controller
                          control={formProps.control}
                          name={"salePriceEnabled"}
                          render={({ field: { value, onChange } }) => (
                            <CheckboxWithLabel checked={value} onCheck={onChange} hidden={!!priceReference}>
                              {t("feature.salesCampaign.specialDealPrice")}
                            </CheckboxWithLabel>
                          )}
                        />
                      )}
                      {isProductComparisonCheckboxVisible(product) && (
                        <ProductComparisonCheckbox productCode={productCode} gaLocation={gaLocation} />
                      )}
                    </Stack>
                  </Stack>
                }
                techTableSlot={<SearchTechValueTable features={productDescriptor.base.features} />}
                buttonsSlot={<Stack gap={2}>{buttons}</Stack>}
              />
              {hasNotifications && (
                <div className={classes.notifications}>
                  {isPackageSizeInvalid && <Alert severity="error">{t("addToCartPopup.mismatchedQty")}</Alert>}
                  {exceedsSalesStock && <Alert severity="info">{t("feature.salesCampaign.exceedsQty")}</Alert>}
                </div>
              )}
            </CardContent>

            {product.quantityVisible && (
              <CardContent
                className={classNames(
                  classes.packagingSelection,
                  productDescriptor.isAvailabilityError && classes.packagingSelectionResetHeight,
                  isProductPMA(productDescriptor) && "hidden",
                )}
              >
                {productDescriptor.isAvailabilityLoading && (
                  <CenterContents>
                    <CircleSpinner />
                  </CenterContents>
                )}
                {!webservicesDisabled && productDescriptor.isAvailabilityError && (
                  <CenterContents>
                    <Alert severity={"error"}>{t("hybrisAvailabilityError")}</Alert>
                  </CenterContents>
                )}

                <div hidden={!productDescriptor.availability || isProductPMA(productDescriptor)}>
                  <PackagingSelection
                    selectedCode={selectedPackageCode}
                    onSelectCode={onSelectPackage}
                    product={productDescriptor}
                    variant={PackagingSelectionVariant.SRP}
                  />
                </div>
              </CardContent>
            )}
            {product.displayMMDAssistant && (
              <MMDAssistant
                isPDP={false}
                marketDesignation={product.marketDesignation}
                initialData={{
                  quantity: quantity,
                  priceReference: priceReference,
                  ...mmdCmnData,
                }}
              />
            )}
          </Card>
        </ProductDescriptorContext>
      </SalesDataProvider>
    </>
  );
};

const CurrentPrice = styled(PriceElements.Price, {
  shouldForwardProp: (prop) => prop !== "soldOut",
})<PriceProps & { soldOut?: boolean }>(({ soldOut, theme }) => ({
  ...getFontStyle(FontSize.HeadlineSmall),
  fontWeight: soldOut ? "normal" : "bold",
  color: soldOut ? theme.palette.text.secondary : theme.palette.text.primary,
  textDecoration: soldOut ? "line-through" : "none",
}));

const OldPrice = styled(PriceElements.Price, {
  shouldForwardProp: (prop) => prop !== "soldOut",
})<PriceProps & { soldOut?: boolean }>(({ soldOut, theme }) => ({
  color: soldOut ? theme.palette.text.primary : theme.palette.text.secondary,
  fontWeight: soldOut ? "bold" : "normal",
  textDecoration: soldOut ? "none" : "line-through",
}));

const StyledSaleEndIndicator = styled(SaleEndIndicator.V2)({
  alignSelf: "flex-start",
});
